import { memo, useEffect, useMemo, useRef, useState } from 'react'
import {
  DndContext,
  DragEndEvent,
  DragMoveEvent,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  closestCenter
} from '@dnd-kit/core'
import {
  groupByLineIndex,
  restrictToSpecificContainer,
  calculateNewPositions,
  updateDropIndicators,
  hideDropIndicators
} from './utils'
import * as Styled from './styled'
import DropLine from './DropLine'
import { IProps, IDndItem } from './types'
import { useCreateSnapModifier } from './hooks'

const MultiLineDnd = ({
  data,
  boundingContainerId,
  dropLineSettings,
  onDragEnd,
  onDragStart,
  isMultiLineAllowed = true
}: IProps) => {
  const [items, setItems] = useState<IDndItem[]>(data)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setItems(data)
  }, [data])

  const {
    createSnapModifier,
    onDragStart: onSnapDragStart,
    hideMagnetizingLine
  } = useCreateSnapModifier(items)

  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: {
      distance: 8
    }
  })

  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: {
      delay: 250,
      tolerance: 5
    }
  })

  const sensors = useSensors(mouseSensor, touchSensor)

  const onDragMove = (event: DragMoveEvent) => {
    const { delta, active, over } = event

    if (!over) return

    const { id: lineIndex } = over as {
      id: number
    }

    if (!containerRef.current) return

    const containerEl = containerRef.current
    const containerWidth = containerEl.clientWidth
    const draggedItem = active.data.current as IDndItem
    if (!draggedItem) return

    const updatedItems = calculateNewPositions({
      draggedItem,
      items,
      dropLineIndex: lineIndex,
      containerWidth,
      deltaX: delta.x
    })

    updatedItems.forEach(item => {
      if (item.id === active.id) {
        return
      }

      const el = containerEl.querySelector(
        `[data-id="${item.id}"]`
      ) as HTMLElement

      if (!el) return

      el.style.left = `${item.left}px`
    })

    const { left, width } = updatedItems.find(
      item => item.id === active.id
    ) as IDndItem

    updateDropIndicators({
      containerRef,
      lineIndex,
      left,
      width
    })
  }

  const _onDragEnd = (event: DragEndEvent) => {
    if (!containerRef.current) return

    hideDropIndicators(containerRef)

    const { delta, active, over } = event
    const { id: lineIndex } = over as { id: number }

    const containerEl = containerRef.current
    const containerWidth = containerEl.clientWidth
    const draggedItem = active.data.current as IDndItem
    if (!draggedItem) return

    const updatedItems = calculateNewPositions({
      draggedItem,
      items,
      dropLineIndex: lineIndex,
      containerWidth,
      deltaX: delta.x
    })

    setItems(updatedItems)
    onDragEnd && onDragEnd(updatedItems)

    hideMagnetizingLine()
  }

  const _onDragStart = () => {
    onSnapDragStart() // Track initial scroll position for magnetizing
    onDragStart && onDragStart()
  }

  const groupedItems = useMemo(() => groupByLineIndex(items), [items])

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={_onDragEnd}
      onDragStart={_onDragStart}
      onDragMove={onDragMove}
      modifiers={[
        ...(boundingContainerId
          ? [
              restrictToSpecificContainer(boundingContainerId),
              createSnapModifier
            ]
          : [])
      ]}
    >
      <Styled.MultiLineDndContainer ref={containerRef}>
        {groupedItems.map((items, index) => (
          <DropLine
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            items={items}
            lineIndex={index}
            {...dropLineSettings}
          />
        ))}
        {/* Change in the future. Try to add lines between each droppable lines */}
        {isMultiLineAllowed ? (
          <DropLine
            items={[]}
            lineIndex={groupedItems.length + 1}
            isAddLine
            {...dropLineSettings}
          />
        ) : null}
      </Styled.MultiLineDndContainer>
    </DndContext>
  )
}

export default memo(MultiLineDnd)
